<template>
    <div class="mt-5" :class="{ 'hasResults': showResults }">
	    <!-- Question -->
	    <div v-if="data.meta" class="d-flex flex-column gap-3 text-center mb-5">
		    <h3 v-if="questionDescription" class="fw-semi-bold m-0" v-html="questionDescription"></h3>
		    <p v-if="questionSubText" class="text-secondary m-0" v-html="questionSubText"></p>
	    </div>

        <!-- Selected Mode (Singular)-->
        <div class="p-1 border border-dark rounded-pill" v-if="data.mode === 'singular' && selectedOptions.length > 0">
            <div class="d-flex align-items-center rounded-pill px-3 bg-dark text-white py-75">
                <i class="me-3 dd-icon bi" v-if="data.meta.icon" :class="data.meta.icon"></i>
                <p class="m-0 ">{{ selectedOptions[0].text }}</p>
                <i class="inline-icon bi bi-x-lg ms-auto" @click="resetSelection"></i>
            </div>
        </div>

	    <div ref="dropdownRoot" class="dropdown-wrapper">
	        <!-- Input -->
	        <div class="d-flex align-items-center justify-content-between rounded-pill border border-secondary py-2 px-3 input-container border-active-darken"
	            :class="{ 'd-none': data.mode === 'singular' && selectedOptions.length > 0, 'border-danger': selectedOptions.length === 0 }">
	            <i class="me-3 dd-icon bi" :class="data.meta.icon" @click="focusInput"></i>
	            <input ref="searchInputEl" type="text" class="form-control m-0 border-0 fs-5 p-0 lh-fixed"
	                placeholder="Search" @focus="searchInputFocus" @input="handleSearchInput" @keyup.enter="blurInput">
	            <i ref="loaderIcon" class="d-none small-icon bi-eye-slash" @click="resetSearch"></i>
	        </div>

	        <!-- Error message -->
	        <p v-if="selectedOptions.length === 0 && !showResults && !data.skippable" class="text-danger my-3">Please select an option</p>

	        <!-- Options -->
	        <div class="d-flex flex-column gap-1 px-0 py-3 gap-1 border border-secondary results overflow-auto"
	            :class="{ 'd-none': !showResults, 'bg-light' : props.data.max_selections && selectedOptions.length >= props.data.max_selections }">
	            <span class="caret"></span>
		        <template v-if="modelName">
			        <p class="my-0 px-3 py-2" :class="{'result-item' : props.data.mode !== 'multiple' || !props.data.max_selections || selectedOptions.length < props.data.max_selections}" v-for="option in options.filter(option => !selectedOptions.some(selected => selected.id === option.id))" @click="() => select(option)">
				        <!--                <img class="rounded avatar me-2" :src="randomImages[Math.floor(Math.random() * randomImages.length)]">-->
				        {{ option.text }}
			        </p>
		        </template>
		        <template v-else>
			        <p class="my-0 px-3 py-2" :class="{'result-item' : props.data.mode !== 'multiple' || !props.data.max_selections || selectedOptions.length < props.data.max_selections}" v-for="option in staticOptionsToDisplay.filter(option => !selectedOptions.some(selected => selected.id === option.id))" @click="() => select(option)">
				        {{ option.text }}
			        </p>
		        </template>
	        </div>
	    </div>

        <!-- Selected Options (Multiple) -->
        <div class="d-flex align-items-center gap-2 mt-3 flex-wrap"
            v-if="data.mode === 'multiple' && selectedOptions.length > 0 && !showResults">
            <button class="btn btn-dark rounded-pill d-flex align-items-center py-2 px-3" v-for="option in selectedOptions">
	            <i class="me-3 dd-icon bi" v-if="data.meta.icon" :class="data.meta.icon"></i>
                <p class="m-0">{{ option.text }}</p>
                <i class="inline-icon bi bi-x ms-3" @click="() => removeOption(option)"></i>
            </button>
        </div>
    </div>
</template>

<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue';
import apiClient from "@/utils/axios.js";
import { useHttpErrorManager } from "@/composables/useHttpErrorManager.js";
import { useDebounceFn, onClickOutside } from "@vueuse/core";

const props = defineProps({
	questionDescription: String,
	questionSubText: String,
    data: Object,
	answer: {
		type: Array,
		default: () => []
	},
	excludes: {
		type: Array,
		default: () => []
	}
});
const emit = defineEmits(['question-answered']);

const showResults = ref(false);
const selectedOptions = ref(props.answer || []);
const loaderIcon = ref(null);
const searchInputEl = ref(HTMLInputElement);
const search = ref('');
const modelName = ref(props.data.model);

const staticOptionsToDisplay = ref(props.data.options || []);
const options = ref([]);
// const randomImages = [
//     'https://images.pexels.com/photos/3689307/pexels-photo-3689307.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
//     'https://images.pexels.com/photos/13564526/pexels-photo-13564526.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
//     'https://images.pexels.com/photos/16441669/pexels-photo-16441669/free-photo-of-mode-kvinna-flicka-sot.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
//     'https://images.pexels.com/photos/12551732/pexels-photo-12551732.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
//     'https://images.pexels.com/photos/13779107/pexels-photo-13779107.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
//     'https://images.pexels.com/photos/4092837/pexels-photo-4092837.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
//     'https://images.pexels.com/photos/9951389/pexels-photo-9951389.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
//     'https://images.pexels.com/photos/14530635/pexels-photo-14530635.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
//     'https://images.pexels.com/photos/8460603/pexels-photo-8460603.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
//     'https://images.pexels.com/photos/16329380/pexels-photo-16329380/free-photo-of-avslappning-glas-lyx-tval.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2',
//     'https://images.pexels.com/photos/13430311/pexels-photo-13430311.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2'
// ]

const debounceSearch = useDebounceFn(async () => {
	await getDataFromBackend();
	searchInputFocus();
}, 500);

const handleSearchInput = (e) => {
    search.value = e.target.value;
	if(modelName.value){
		debounceSearch();
	}
}

const searchInputFocus = () => {
	if (modelName.value && options.value.length > 0) {
		showResults.value = true;
		loaderIcon.value.classList.remove('d-none')
	}
	else if(!modelName.value && options.value.length > 0){
		showResults.value = true;
		loaderIcon.value.classList.remove('d-none')
	}
}

const focusInput = () => {
    searchInputEl.value.focus();
}

const blurInput = () => {
    searchInputEl.value.blur();
}

const resetSearchInput = () => {
    blurInput();
    // searchInputEl.value.value = '';
    // search.value = '';
    loaderIcon.value.classList.add('d-none')
    showResults.value = false;
}

const resetSearch = (e = null) => {
    resetSearchInput();
    if (props.data.mode === 'singular')
        selectedOptions.value = [];
}

const select = (val) => {
	if(props.data.max_selections && selectedOptions.value.length >= props.data.max_selections){
		return;
	}
    selectedOptions.value.push(val);
    emit('question-answered', selectedOptions.value);
    if (props.data.mode === 'multiple') {
        setTimeout(focusInput, 100)
    }
	else {
	    loaderIcon.value.classList.add('d-none')
	    showResults.value = false;
	}
}

const resetSelection = () => {
	selectedOptions.value = [];
	showResults.value = true;
	focusInput();
	loaderIcon.value.classList.remove('d-none')
}

const removeOption = (val) => {
    selectedOptions.value = selectedOptions.value.filter(o => o.id !== val.id);
    emit('question-answered', selectedOptions.value);
}

const dropdownRoot  = ref(null);
onClickOutside(dropdownRoot , () => {
	if (showResults.value) {
		loaderIcon.value.classList.add('d-none');
		showResults.value = false;
	}
});

const getDataFromBackend = async () => {
	try {
		showResults.value = false;
		if(loaderIcon.value){
			loaderIcon.value.classList.remove('d-none')
			loaderIcon.value.classList.add('loading')
		}

		let response = await apiClient.post('member/surveys/modelData', {
			model_name: modelName.value,
			keyword: search.value,
			exclude: props.excludes
		});
		if (response.status === 200) {
			options.value = response.data.data.options;
		}
	}
	catch (error) {
		let errorObj = await useHttpErrorManager().handleError(error, true);
		options.value = [];
		showResults.value = false;
		if(loaderIcon.value){
			loaderIcon.value.classList.add('d-none')
		}
	}
	finally {
		if(loaderIcon.value){
			loaderIcon.value.classList.remove("loading");
		}
	}
}

watch(modelName, (newVal, oldVal) => {
	if (newVal !== oldVal && newVal) {
		getDataFromBackend();
	}
	else{
		options.value = props.data.options || [];
	}
}, { immediate: true });

watch(selectedOptions, (newVal, oldVal) => {
	emit('question-answered', newVal);
});

watch(search, (val) => {
	if (val.trim().length === 0 && loaderIcon.value && modelName.value) {
		loaderIcon.value.classList.add('d-none')
		if(modelName.value){
			debounceSearch();
		}
	}
	if (val && val.trim().length > 0 && !modelName.value) {
		if(loaderIcon.value){
			loaderIcon.value.classList.remove('d-none')
			loaderIcon.value.classList.add('loading')
		}

		setTimeout(() => {
			if(loaderIcon.value){
				loaderIcon.value.classList.remove("loading");
			}
			staticOptionsToDisplay.value = props.data.options.filter(option => option.text.toLowerCase().includes(val.toLowerCase()));
			showResults.value = true;
		}, 100)
	}
	if( val.trim().length === 0 && !modelName.value){
		staticOptionsToDisplay.value = props.data.options || [];
	}
})

</script>

<style scoped lang="scss">
.py-75 {
    padding-top: 0.75em !important;
    padding-bottom: 0.75em !important;
}

.form-control {
    transition: none !important;
}

.lh-fixed {
    line-height: 1.5 !important;
}

.dd-icon {
    font-size: 24px;
    width: auto;
    height: auto;
    padding: 0;
}

.hasResults {
    .results {
        border: 0 !important;
        border-radius: .9em;
        margin-top: 0.75em;
        box-shadow: 0 0.1rem 0.7rem 0 rgba(0, 0, 0, 0.175);
        position: relative;
        z-index: 10;
        max-height: calc(100dvh - 400px);

        .result-item {
            cursor: pointer;

            &:hover {
                background-color: #f5f5f7;
            }
        }

        .caret {
            content: '';
            position: absolute;
            display: block;
            width: 12px;
            height: 12px;
            box-shadow: -3px -3px 0.4rem 0 rgba(0, 0, 0, 0.13);
            background-color: #fff;
            z-index: -1;
            top: 0;
            left: 18px;
            -webkit-transform: translateY(-50%) rotate(45deg);
            transform: translateY(-50%) rotate(45deg);
        }
    }
}
</style>